import { useRef, useState, useEffect } from 'react';
import {
  Badge,
  Box,
  Button,
  HStack,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import axiosInstance from '../../utils/axiosInstance';
import axios from 'axios';

export default function Cover({ coverImageUrl, userId, isEditable }) {
  const [currentCoverUrl, setCurrentCoverUrl] = useState(coverImageUrl); // Local state for cover URL
  const inputRef = useRef(null);
  const { isOpen, onOpen, onClose } = useDisclosure(); // For handling error modals
  const [imageLoaded, setImageLoaded] = useState(false); // Flag for image loading

  const backendUrl = process.env.NODE_ENV === 'production'
    ? process.env.REACT_APP_BACKEND_URL
    : 'http://localhost:8080';

  // Open file input dialog
  const openChooseFile = () => {
    inputRef.current.click();
  };

  // Function to change the cover image preview locally
  const changeCoverPreview = (event) => {
    const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/jpg'];
    const selected = event.target.files[0];

    if (selected && ALLOWED_TYPES.includes(selected.type)) {
      const reader = new FileReader();
      reader.onloadend = () => setCurrentCoverUrl(reader.result); // Update the preview
      reader.readAsDataURL(selected);
    } else {
      console.error('Invalid file type');
      onOpen(); // Trigger error modal for unsupported types
    }
  };

  // Uploads the selected cover image
  const handleChangeCover = async (event) => {
    event.preventDefault(); // Prevent page refresh

    const selectedFile = event.target.files[0]; // Access the selected file
    if (selectedFile) {
      try {
        // Step 1: Get signed URL from backend
        const fileType = selectedFile.type;
        const fileName = selectedFile.name;
        const response = await axiosInstance.put(
          `${backendUrl}/user/update-cover/${userId}/${fileName}?file-type=${fileType}`
        );
        console.log("Upload", response.data)

        const uploadUrl = response.data.uploadUrl;
        const newCoverImageUrl = response.data.coverImageUrl;

        if (!uploadUrl) {
          console.error('Missing upload URL or blob URL');
          onOpen(); // Trigger error modal if URLs are missing
          return;
        }

        // Step 2: Upload image to blob storage using signed URL
        const upload = await axios.put(uploadUrl, selectedFile, {
          headers: {
            'Content-Type': fileType,
          },

        });
        console.log(upload)

        if (newCoverImageUrl) {
          console.log("Cover image updated successfully");
        } else {
          console.error('Invalid cover image URL');
          onOpen(); // Trigger error modal if URL is not valid
        }
      } catch (error) {
        console.error('Failed to upload cover image:', error);
        onOpen(); // Trigger error modal on failure
      }
    }
  };

  // Fetch the cover image from the cloud (e.g., Vercel Blob or GCP)
  {/*
  const fetchCoverImage = async (fileName) => {
    // Check if fileName is empty or undefined
    if (!fileName) {
      console.log('No fileName provided, skipping fetch request');
      return; // Skip the request
    }

    try {
      // Cover Images can be public at least thru app since we are using GCP signed URL
      const { data } = await axios.get(`${backendUrl}/get-cover/${userId}/${fileName}`);

      if (data.url) {
        setCurrentCoverUrl(data.url); // Update the current cover URL
      }
    } catch (error) {
      console.error('Failed to fetch cover image:', error);
    }
  };

  // Load the cover image on component mount
  useEffect(() => {
    fetchCoverImage(currentCoverUrl);
  }, [userId]);*/}

  return (
    <>
      <style>{`
  .container {
    position: absolute;
    inset: -1em;
    --c: 7px;
    background-color: #000;
    background-image: radial-gradient(
        circle at 50% 50%,
        #0000 1.5px,
        #000 0 var(--c),
        #0000 var(--c)
      ),
      radial-gradient(
        circle at 50% 50%,
        #0000 1.5px,
        #000 0 var(--c),
        #0000 var(--c)
      ),
      radial-gradient(circle at 50% 50%, #f00, #f000 60%),
      radial-gradient(circle at 50% 50%, #ff0, #ff00 60%),
      radial-gradient(circle at 50% 50%, #0f0, #0f00 60%),
      radial-gradient(ellipse at 50% 50%, #00f, #00f0 60%);
    background-size:
      12px 20.7846097px,
      12px 20.7846097px,
      200% 200%,
      200% 200%,
      200% 200%,
      200% 20.7846097px;
    --p: 0px 0px, 6px 10.39230485px;
    background-position:
      var(--p),
      0% 0%,
      0% 0%,
      0% 0px;
    animation:
      wee 40s linear infinite,
      filt 6s linear infinite;
  }

  @keyframes filt {
    0% { filter: hue-rotate(0deg); }
    100% { filter: hue-rotate(360deg); }
  }

  @keyframes wee {
    0% {
      background-position:
        var(--p),
        800% 400%,
        1000% -400%,
        -1200% -600%,
        400% 41.5692194px;
    }
    100% {
      background-position:
        var(--p),
        0% 0%,
        0% 0%,
        0% 0%,
        0% 0%;
    }
  }
`}</style>
      <Box
        position="relative"
        h="250px"
        overflow="hidden"
        mt={0}
        borderRadius="md"
        borderTopRadius={0}
        mb={0}
      >
        {/* Display the cover image */}
        <Box w="full" h="full" position="relative">
          {currentCoverUrl ? (
            <Image
              w="full"
              h="full"
              objectFit="cover"
              src={currentCoverUrl}
              alt="Cover"
              position="absolute"
              top={0}
              left={0}
              onError={() => setCurrentCoverUrl(null)} // fallback trigger
              onLoad={() => setImageLoaded(true)}
            />
          ) : (
            <Box className="container" />
          )}
        </Box>


        {/* Show upload button only if editable */}
        {isEditable && (
          <Button
            onClick={openChooseFile}
            position="absolute"
            top={4}
            right={4}
            variant="ghost"
            colorScheme="whiteAlpha"
            background="rgba(0, 0, 0, 0.5)"
            _hover={{ background: 'rgba(0, 0, 0, 0.7)' }}
            _active={{ background: 'rgba(0, 0, 0, 0.8)' }}
          >
            <svg width="1.2em" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M4 5a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V7a2 2 0 00-2-2h-1.586a1 1 0 01-.707-.293l-1.121-1.121A2 2 0 0011.172 3H8.828a2 2 0 00-1.414.586L6.293 4.707A1 1 0 015.586 5H4zm6 9a3 3 0 100-6 3 3 0 000 6z"
              />
            </svg>
            <Text ml={2} color="white">Change Cover</Text>
            <input
              ref={inputRef}
              type="file"
              onChange={(e) => { changeCoverPreview(e); handleChangeCover(e); }}
              hidden
            />
          </Button>
        )}

        {/* Modal for unsupported file type */}
        <Modal isOpen={isOpen} onClose={onClose}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Something went wrong</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Text>File not supported!</Text>
              <HStack mt={1}>
                <Text color="brand.cadet" fontSize="sm">
                  Supported types:
                </Text>
                <Badge colorScheme="green">PNG</Badge>
                <Badge colorScheme="green">JPG</Badge>
                <Badge colorScheme="green">JPEG</Badge>
              </HStack>
            </ModalBody>
            <ModalFooter>
              <Button onClick={onClose}>Close</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Box>
    </>
  );
}

<style>{`
  .container {
    position: absolute;
    inset: -1em;
    --c: 7px;
    background-color: #000;
    background-image: radial-gradient(
        circle at 50% 50%,
        #0000 1.5px,
        #000 0 var(--c),
        #0000 var(--c)
      ),
      radial-gradient(
        circle at 50% 50%,
        #0000 1.5px,
        #000 0 var(--c),
        #0000 var(--c)
      ),
      radial-gradient(circle at 50% 50%, #f00, #f000 60%),
      radial-gradient(circle at 50% 50%, #ff0, #ff00 60%),
      radial-gradient(circle at 50% 50%, #0f0, #0f00 60%),
      radial-gradient(ellipse at 50% 50%, #00f, #00f0 60%);
    background-size:
      12px 20.7846097px,
      12px 20.7846097px,
      200% 200%,
      200% 200%,
      200% 200%,
      200% 20.7846097px;
    --p: 0px 0px, 6px 10.39230485px;
    background-position:
      var(--p),
      0% 0%,
      0% 0%,
      0% 0px;
    animation:
      wee 40s linear infinite,
      filt 6s linear infinite;
  }

  @keyframes filt {
    0% { filter: hue-rotate(0deg); }
    100% { filter: hue-rotate(360deg); }
  }

  @keyframes wee {
    0% {
      background-position:
        var(--p),
        800% 400%,
        1000% -400%,
        -1200% -600%,
        400% 41.5692194px;
    }
    100% {
      background-position:
        var(--p),
        0% 0%,
        0% 0%,
        0% 0%,
        0% 0%;
    }
  }
`}</style>